import I18n from 'I18n';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
// @ts-expect-error no types
import * as SimpleDate from 'UIComponents/core/SimpleDate';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import HR from 'UIComponents/elements/HR';
import H2 from 'UIComponents/elements/headings/H2';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILink from 'UIComponents/link/UILink';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import { useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { SubscriptionStatus } from '../../types/subscriptions';
import { EMPTY_VALUE, TEST_IDS, URLS } from '../../utils/constants';
import { createSimpleDate } from '../../utils/dateTime';
import { handleError } from '../../utils/errorHandling';
import { useTracker } from '../../utils/usageTracker';
import { StyledSmallObsidian, StyledUITile } from '../components/styledComponents';
import { useResumeSubscription } from './useResumeSubscription';
import { useResumeSubscriptionQuery } from './useResumeSubscriptionQuery';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function getDescriptionMessage(collectionProcess, statusAfterUnpause) {
  if (statusAfterUnpause === SubscriptionStatus.Unpaid) {
    return /*#__PURE__*/_jsx(UIAlert, {
      className: "p-x-4",
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `subscriptions.ResumeSubscriptionModal.description.unpaidWarning.${collectionProcess}_jsx`,
        options: {
          status: statusAfterUnpause,
          external: true,
          href: URLS.KBUpdatePaymentMethod
        },
        elements: {
          Link: UILink
        }
      })
    });
  }
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `subscriptions.ResumeSubscriptionModal.description.${collectionProcess}_jsx`,
    options: {
      status: statusAfterUnpause
    }
  });
}
export function ResumeSubscriptionModal({
  actions,
  objectId
}) {
  var _buyer$email;
  const {
    close
  } = actions;
  const {
    trackInteraction
  } = useTracker();
  const {
    lastPaymentDate,
    buyer,
    checkoutEmail,
    collectionProcess
  } = useSubscription();
  const customerEmail = (_buyer$email = buyer === null || buyer === void 0 ? void 0 : buyer.email) !== null && _buyer$email !== void 0 ? _buyer$email : checkoutEmail;
  const {
    data: queryData,
    loading: queryLoading
  } = useResumeSubscriptionQuery(objectId);
  const [proposedBillingDate, setProposedBillingDate] = useState(0);
  const [onSubmit, {
    loading: submitLoading
  }] = useResumeSubscription({
    objectId,
    proposedNextBillingDate: proposedBillingDate,
    onClose: async () => {
      close();
      await actions.revalidate();
    }
  });
  if (queryLoading || !queryData) {
    return /*#__PURE__*/_jsx(UIModal, {
      "data-test-id": TEST_IDS.Resume,
      children: /*#__PURE__*/_jsxs(UIForm, {
        children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
          children: [/*#__PURE__*/_jsx(H2, {
            className: "p-y-4",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.ResumeSubscriptionModal.title"
            })
          }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
            onClick: close
          })]
        }), /*#__PURE__*/_jsx(UIDialogBody, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(UIAbstractProgress, {
              Component: UINanoProgress,
              maximum: 35
            }), /*#__PURE__*/_jsx(UILoadingSpinner, {
              size: "small",
              minHeight: 100,
              layout: "centered",
              grow: true,
              showLabel: true,
              "data-test-id": `${TEST_IDS.Resume}:loading`,
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.ResumeSubscriptionModal.loadingMessage"
              })
            })]
          })
        })]
      })
    });
  }
  const {
    earliestBillingDate,
    proposedNextBillingDate,
    statusAfterUnpause
  } = queryData;
  if (proposedNextBillingDate && proposedBillingDate === 0) {
    setProposedBillingDate(proposedNextBillingDate);
  }
  return /*#__PURE__*/_jsx(UIModal, {
    "data-test-id": TEST_IDS.Resume,
    children: /*#__PURE__*/_jsxs(UIForm, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          className: "p-y-4",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.title"
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: () => {
            trackInteraction('resumeSubscriptionModalClosed');
            close();
          }
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [getDescriptionMessage(collectionProcess, statusAfterUnpause), /*#__PURE__*/_jsx(HR, {
          className: "m-top-4 m-bottom-4"
        }), /*#__PURE__*/_jsx(StyledUITile, {
          className: "p-x-4 p-y-2 m-bottom-3",
          children: /*#__PURE__*/_jsxs(UIGrid, {
            className: "m-y-2",
            children: [/*#__PURE__*/_jsxs(UIGridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(StyledSmallObsidian, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: 'subscriptions.ResumeSubscriptionModal.lastBillingDate'
                })
              }), /*#__PURE__*/_jsx("p", {
                className: "m-bottom-0",
                children: !lastPaymentDate ? /*#__PURE__*/_jsx("span", {
                  children: EMPTY_VALUE
                }) : /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(lastPaymentDate),
                  format: "ll"
                })
              })]
            }), /*#__PURE__*/_jsxs(UIGridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(StyledSmallObsidian, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.ResumeSubscriptionModal.nextBillingDate"
                })
              }), /*#__PURE__*/_jsx("p", {
                className: "m-bottom-0",
                children: /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(proposedNextBillingDate),
                  format: "ll"
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.datePicker.title"
          }),
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.datePicker.tooltip"
          }),
          children: /*#__PURE__*/_jsx(UIDateInput, {
            "data-test-id": `${TEST_IDS.Resume}:date-picker`,
            min: createSimpleDate(earliestBillingDate),
            minValidationMessage: I18n.text('subscriptions.ResumeSubscriptionModal.datePicker.minValidationMessage'),
            onChange: ({
              currentTarget: {
                value
              }
            }) => {
              const proposedDate = SimpleDate.toMomentUTC(value).valueOf();
              if (!proposedDate) {
                setProposedBillingDate(proposedNextBillingDate);
              } else {
                setProposedBillingDate(proposedDate);
              }
            }
          })
        }), /*#__PURE__*/_jsx(HR, {
          className: "m-top-4 m-bottom-4"
        }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "subscriptions.ResumeSubscriptionModal.description_jsx",
          options: {
            href: URLS.KBEmailConfirmation,
            email: customerEmail,
            external: true
          },
          elements: {
            Link: UILink
          }
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          "data-test-id": `${TEST_IDS.Resume}:submit`,
          use: "primary",
          loading: submitLoading,
          onClick: () => {
            onSubmit().catch(e => handleError(e));
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.resumeButton"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": `${TEST_IDS.Resume}:cancel`,
          use: "secondary",
          onClick: () => {
            trackInteraction('resumeSubscriptionModalClosed');
            close();
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.cancelButton"
          })
        })]
      })]
    })
  });
}